// src/store/slices/cartSlice.ts
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API_ROUTE } from '../../utils/commonConstants';
import { getDiscountedPrice } from '../../utils/formatters';

// Define the initial state type

const cachedCartItems = () => {
    // if (typeof window !== 'undefined') {
    //     let cartString = localStorage.getItem('cartCache');

    //     if (cartString) {
    //         return JSON.parse(cartString);
    //     } else {
    //         return [];
    //     }
    // } else {
        return [];
    // }
}

const prepareCartItems = (fetchedCartItems) => {
    const cartItems = fetchedCartItems;
    let totalPrice = 0;
    let totalDiscount = 0;
    const orderItems = [];

    for (const item of cartItems) {
        const { productId, quantity } = item;

        orderItems.push({
            productId,
            inventoryId: item.inventoryId,
            quantity: quantity,
            price: item.inventory.price,
        });

        totalPrice += quantity * item.inventory.price;
        totalDiscount += quantity * item.inventory.discount;
    }

    return { cartItems, totalPrice, orderItems, totalDiscount };
}

// Initial state
const initialState = {
    total: '0.00',
    totalDiscount: 0,
    isCartOpen: false,
    cartItems: cachedCartItems(),
    loading: { fetching: false, deleting: false, saving: false },
    error: null,
    shipping: null,
    paymentMethod: 'COD',
    isStandAlone: true,
    discount: 0,
    netDiscount: 0
};

const cartSlice = createSlice({
    name: 'counter',
    initialState,
    reducers: {
        setShipping: (state, action) => {
            state.shipping = action.payload;
        },

        setTotal: (state, action) => {
            state.total = action.payload;
        },

        setDiscount: (state, action) => {
            state.discount = action.payload;
            const { totalPrice, totalDiscount } = prepareCartItems(state.cartItems, state.discount);
            state.total = totalPrice.toFixed(2);
            state.totalDiscount = (totalDiscount + action.payload).toFixed(2);
        },

        setIsStandAlone: (state, action) => {
            state.isStandAlone = action.payload;
        },

        setPaymentMethod: (state, action) => {
            state.paymentMethod = action.payload;
        },

        setCartOpen: (state, action) => {
            state.isCartOpen = action.payload;
        },

        setCartItems: (state, action) => {
            state.cartItems = action.payload;
        },

        loadCachedCartItems: (state) => {
            state.cartItems = cachedCartItems();
        },

        calculateCartTotal: (state) => {
            const { totalPrice, totalDiscount } = prepareCartItems(state.cartItems);
            state.total = totalPrice.toFixed(2);
            state.totalDiscount = (totalDiscount + state.discount).toFixed(2);
        },

        addToCart: (state, action) => {
            const product = action.payload.product;
            const inventory = action.payload.inventory;
            const item = action.payload;
            const cartItem = {
                productId: product.id,
                inventoryId: inventory.id,
                quantity: item.quantity,
                price: item.price, 
                amount: 0, // This should be calculated carefully
                product,
                inventory
            }
            const existingItemOnCart = state.cartItems.find((item) => item.inventoryId === cartItem.inventoryId);

            // if (inventory) {
            //     cartItem.product.stock = inventory.stock;
            //     cartItem.inventoryId = inventory.id;
            // }

            if (!existingItemOnCart) {
                const cartItems = [...state.cartItems, cartItem];
                localStorage.setItem('cartCache', JSON.stringify(cartItems));
                const { totalPrice, totalDiscount } = prepareCartItems(cartItems);
                state.total = totalPrice.toFixed(2);
                state.totalDiscount = (totalDiscount + state.discount).toFixed(2);
                state.cartItems = cartItems;
            } else {
                let updatedQuantity = 0;
                const updatedCartItems = state.cartItems.map(item => {
                    if (item.inventoryId === existingItemOnCart.inventoryId) {
                        updatedQuantity = item.quantity + 1;

                        if (updatedQuantity > 0 && updatedQuantity <= item.inventory.stock) {
                            item.quantity = updatedQuantity;
                        }
                    }

                    return item;
                })

                localStorage.setItem('cartCache', JSON.stringify(updatedCartItems));
                const { totalPrice, cartItems, totalDiscount } = prepareCartItems(updatedCartItems);
                state.cartItems = cartItems;
                state.total = totalPrice.toFixed(2);
                state.totalDiscount = (totalDiscount + state.discount).toFixed(2);
            }
        },

        updateCartItemQuantity: (state, action) => {
            let updatedQuantity = 0;
            const updatedCartItems = state.cartItems.map(item => {
                if (item.inventoryId === action.payload.cartItem.inventoryId) {
                    updatedQuantity = item.quantity + action.payload.quantity;

                    if (updatedQuantity > 0 && updatedQuantity <= item.inventory.stock) {
                        item.quantity = updatedQuantity;
                    }
                }

                return item;
            })

            localStorage.setItem('cartCache', JSON.stringify(updatedCartItems));
            const { totalPrice, cartItems, totalDiscount } = prepareCartItems(updatedCartItems);
            state.cartItems = cartItems;
            state.total = totalPrice.toFixed(2);
            state.totalDiscount = (totalDiscount + state.discount).toFixed(2);
        },

        removeFromCart: (state, action) => {
            const cartItem = action.payload;
            const cartItems = state.cartItems.filter((item) => item.inventoryId !== cartItem.inventoryId);

            localStorage.setItem('cartCache', JSON.stringify(cartItems));
            const { totalPrice, totalDiscount } = prepareCartItems(cartItems);
            state.cartItems = cartItems;
            state.total = totalPrice.toFixed(2);
            state.totalDiscount = (totalDiscount + state.discount).toFixed(2);
        },

        clearCart: (state) => {
            localStorage.setItem('cartCache', JSON.stringify([]));
            return {...initialState, cartItems: []};
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch data list
            .addCase(fetchCartItems.pending, (state) => {
                state.loading.fetching = true;
                state.error = null;
            })
            .addCase(fetchCartItems.fulfilled, (state, action) => {
                state.loading.fetching = false;

                if (action.payload?.authFailed) {
                    // signOut({callbackUrl: '/signin'});
                } else {
                    const { totalPrice, cartItems, totalDiscount } = prepareCartItems(action.payload);
                    state.cartItems = cartItems;
                    state.total = totalPrice.toFixed(2);
                    state.totalDiscount = (totalDiscount + state.discount).toFixed(2);
                }
            })
            .addCase(fetchCartItems.rejected, (state, action) => {
                state.loading.fetching = false;
                state.error = action.payload;
            })

            // Add cart item
            .addCase(addCartItem.pending, (state) => {
                state.loading.saving = true;
                state.error = null;
            })
            .addCase(addCartItem.fulfilled, (state, action) => {
                state.loading.saving = false;
            })
            .addCase(addCartItem.rejected, (state, action) => {
                state.loading.saving = false;
                state.error = action.payload;
            })

            // Delete cart item
            .addCase(deleteCartItem.pending, (state) => {
                state.loading.deleting = true;
                state.error = null;
            })
            .addCase(deleteCartItem.fulfilled, (state, action) => {
                state.loading.deleting = false;
            })
            .addCase(deleteCartItem.rejected, (state, action) => {
                state.loading.deleting = false;
                state.error = action.payload;
            })

            // Delete cart items
            .addCase(deleteCartItems.pending, (state) => {
                state.loading.deleting = true;
                state.error = null;
            })
            .addCase(deleteCartItems.fulfilled, (state, action) => {
                state.loading.deleting = false;
            })
            .addCase(deleteCartItems.rejected, (state, action) => {
                state.loading.deleting = false;
                state.error = action.payload;
            })

            // Update cart item
            .addCase(updateCartItem.pending, (state) => {
                state.loading.saving = true;
                state.error = null;
            })
            .addCase(updateCartItem.fulfilled, (state, action) => {
                state.loading.saving = false;
            })
            .addCase(updateCartItem.rejected, (state, action) => {
                state.loading.saving = false;
                state.error = action.payload;
            })
    }
});

export const fetchCartItems = createAsyncThunk(
    "cartSlice/fetchCartItems",
    async (_, { rejectWithValue, dispatch }) => {
        try {
            // const session = await getSession();
            // const result = await fetch(API_ROUTE + "/cart", {
            //     method: "GET",
            //     headers: {
            //         "Authorization": "Bearer " + session?.user?.token,
            //         "Content-Type": "application/json"
            //     }
            // });

            // if (!result.ok) {
            //     signOut({callbackUrl: '/signin'});
            //     throw new Error('Failed to delete cart item');
            // }

            // return result.json();
            return null;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to fetch data");
        }
    }
);

export const addCartItem = createAsyncThunk(
    "cartSlice/addCartItem",
    async ({ cartItem }, { rejectWithValue }) => {
        try {
            // const session = await getSession();
            // const result = await fetch(API_ROUTE + "/cart/add", {
            //     method: "POST",
            //     body: JSON.stringify({ productId: cartItem.productId, quantity: cartItem.quantity }),
            //     headers: {
            //         "Authorization": "Bearer " + session?.user?.token,
            //         "Content-Type": "application/json"
            //     }
            // });

            // return result.json();
            return null;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to fetch data");
        }
    }
);

export const updateCartItem = createAsyncThunk(
    "cartSlice/updateCartItem",
    async ({ cartItem }, { rejectWithValue }) => {
        try {
            // const session = await getSession();
            // const result = await fetch(API_ROUTE + "/cart/add", { // same for update also
            //     method: "POST",
            //     body: JSON.stringify({ productId: cartItem.productId, quantity: cartItem.quantity }),
            //     headers: {
            //         "Authorization": "Bearer " + session?.user?.token,
            //         "Content-Type": "application/json"
            //     }
            // });

            // return result.json();
            return null;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to fetch data");
        }
    }
);

export const deleteCartItem = createAsyncThunk(
    "cartSlice/deleteCartItem",
    async ({ productId }, { rejectWithValue }) => {
        try {
            // const session = await getSession();
            // const result = await fetch(API_ROUTE + "/cart/remove/" + productId, {
            //     method: "DELETE",
            //     headers: {
            //         "Authorization": "Bearer " + session?.user?.token,
            //         "Content-Type": "application/json"
            //     }
            // });

            // return result.json();
            return null;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to fetch data");
        }
    }
);

export const deleteCartItems = createAsyncThunk(
    "cartSlice/deleteCartItems",
    async (_, { rejectWithValue }) => {
        try {
            // const session = await getSession();
            // const result = await fetch(API_ROUTE + "/cart/clear", {
            //     method: "DELETE",
            //     headers: {
            //         "Authorization": "Bearer " + session?.user?.token,
            //         "Content-Type": "application/json"
            //     }
            // });

            // return result.json();
            return null;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to delete data");
        }
    }
);

export const {
    clearCart,
    setTotal,
    setCartOpen,
    addToCart,
    removeFromCart,
    updateCartItemQuantity,
    setShipping,
    setPaymentMethod,
    setCartItems,
    loadCachedCartItems,
    setIsStandAlone,
    calculateCartTotal,
    setDiscount
} = cartSlice.actions;
export default cartSlice.reducer;
